import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { Flex } from '@radix-ui/themes'
import { PlayWalletStatus } from 'shared/store/play-wallet'
import { NeonButton } from 'shared/ui/neon'
import { shortenAddress } from 'shared/utils/shorten-address'
import styles from '../neon-wallets.module.scss'

interface Props {
  status: PlayWalletStatus | null
  connectedAddress: string | null
  isLinking: boolean
  onLinkConnected: () => void
  onSelectConnected: (address: string) => void
}

interface BannerProps {
  tone: 'pink' | 'cyan'
  title: string
  text: string
  action: React.ReactNode
}

const NoticeBanner: React.FC<BannerProps> = ({ tone, title, text, action }) => (
  <Flex
    direction='column'
    gap='2'
    className={styles.notice}
    data-tone={tone}
  >
    <Flex
      align='center'
      gap='2'
    >
      <ExclamationTriangleIcon className={styles.noticeIcon} />
      <span className={styles.noticeTitle}>{title}</span>
    </Flex>
    <span className={styles.noticeText}>{text}</span>
    {action}
  </Flex>
)

/** Explains why game actions are blocked for the connected wallet. */
export const PlayWalletNotice: React.FC<Props> = ({
  status,
  connectedAddress,
  isLinking,
  onLinkConnected,
  onSelectConnected
}) => {
  if (!connectedAddress || !status || status === PlayWalletStatus.ready) {
    return null
  }

  const shortAddress = shortenAddress(connectedAddress)

  if (status === PlayWalletStatus.notLinked) {
    return (
      <NoticeBanner
        tone='pink'
        title={`The connected wallet ${shortAddress} is not linked to your account`}
        text='Games are blocked for this wallet. Anything it plays is recorded for a separate anonymous account, and the wallet can then never be linked to yours. Link it first, or switch to a linked wallet in your wallet app.'
        action={
          <NeonButton
            type='button'
            color='pink'
            disabled={isLinking}
            onClick={onLinkConnected}
          >
            Link {shortAddress}
          </NeonButton>
        }
      />
    )
  }

  return (
    <NoticeBanner
      tone='cyan'
      title={`The connected wallet ${shortAddress} is not the one selected for play`}
      text='Switch to the selected wallet in your wallet app, or play with the connected one.'
      action={
        <button
          type='button'
          className={styles.secondaryButton}
          onClick={() => onSelectConnected(connectedAddress)}
        >
          Play with {shortAddress}
        </button>
      }
    />
  )
}
